import dayjs from '~/timeInit';

import { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-pro';

import { getDefaultDateRange } from '~/components/GHAParticipationReport/utils';

import type { Dayjs } from '~/services/TimeService';

import type {
  CompetitorHotelOpportunities,
  CompetitorProperty,
  CompetitorPropertyHistoryEntry,
  CompetitorPropertyOption,
  CompetitorPropertySnapshot,
  CompetitorSourceName,
  LeFlashStatus,
} from '~/types/services/priceParrot';

import currencyFormatter, { addDollarType } from '~/utils/currencyFormatter';

import {
  AUTO_VERIFIED_MAX_MATCH_TIER,
  COMPETITOR_FLASH_STATUS_LABELS,
  COMPETITOR_OFFERS_PATH,
  COMPETITOR_OFFERS_PRESETS,
  COMPETITOR_SOURCE_LABELS,
  COMPETITOR_SOURCE_NAMES,
  CompetitorFlashStatus,
  CompetitorOffersPreset,
  DATE_FORMAT,
  FLASH_MATCH_LABELS,
  FlashMatch,
  LE_FLASH_STATUS_LABELS,
} from './constants';

export function supplierKey(property: Pick<CompetitorProperty, 'sourceName' | 'externalPropertyId'>) {
  return `${property.sourceName}:${property.externalPropertyId}`;
}

// The competitor listings matched as the same hotel, ordered by competitor.
export function hotelSuppliers(properties: Array<CompetitorProperty>, property: CompetitorProperty) {
  if (!property.hotelKey) {
    return [property];
  }
  return properties
    .filter((candidate) => candidate.hotelKey === property.hotelKey)
    .sort(
      (a, b) =>
        COMPETITOR_SOURCE_NAMES.indexOf(a.sourceName) - COMPETITOR_SOURCE_NAMES.indexOf(b.sourceName) ||
        a.externalPropertyId.localeCompare(b.externalPropertyId),
    );
}

// The competitor's name, plus the hotel name when that competitor lists the hotel more than once.
export function supplierLabel(supplier: CompetitorProperty, suppliers: Array<CompetitorProperty>) {
  const label = COMPETITOR_SOURCE_LABELS[supplier.sourceName];
  const sameSource = suppliers.filter((candidate) => candidate.sourceName === supplier.sourceName);
  return sameSource.length > 1 ? `${label} · ${supplier.hotelName ?? supplier.externalPropertyId}` : label;
}

export function competitorOfferPath(property: Pick<CompetitorProperty, 'sourceName' | 'externalPropertyId'>) {
  return `${COMPETITOR_OFFERS_PATH}/${property.sourceName}/${encodeURIComponent(property.externalPropertyId)}`;
}

// AUD as A$; other currencies by code so a foreign dollar is never mistaken for AUD.
export function formatAmount(amount: number | undefined, currency: string | undefined): string {
  if (amount === undefined || !currency) {
    return '—';
  }
  const fractionDigits = Number.isInteger(amount) ? 0 : 2;
  if (currency === 'AUD') {
    return addDollarType(currencyFormatter(currency, amount, fractionDigits), currency);
  }
  return currencyFormatter(currency, amount, fractionDigits, 'code').replace(/\u00a0/g, ' ');
}

// The competitor's own currency first; AUD alongside when it differs.
export function formatAmountWithAud(
  amount: number | undefined,
  currency: string | undefined,
  amountAud: number | undefined,
): string {
  const original = formatAmount(amount, currency);
  if (currency === 'AUD' || amountAud === undefined) {
    return original;
  }
  return `${original} · ${formatAmount(Math.round(amountAud), 'AUD')}`;
}

export function taxBasisLabel(includesTax: boolean | undefined): string {
  if (includesTax === undefined) {
    return 'tax basis unknown';
  }
  return includesTax ? 'incl. tax' : 'excl. tax';
}

export function formatDate(value: string | undefined): string {
  return value ? dayjs(value).format(DATE_FORMAT) : '—';
}

export function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

export function isAutoVerifiedMatch(matchTier: number | undefined): boolean {
  return matchTier !== undefined && matchTier <= AUTO_VERIFIED_MAX_MATCH_TIER;
}

interface StayPrice {
  priceExclTax?: number;
  tax?: number;
  priceInclTax?: number;
  currency?: string;
  priceExclTaxAud?: number;
  priceInclTaxAud?: number;
}

// Tax-inclusive where known; otherwise the before-tax price, flagged as such.
export function formatStayPrice(stay: StayPrice): string {
  if (stay.priceInclTax !== undefined) {
    return formatAmountWithAud(stay.priceInclTax, stay.currency, stay.priceInclTaxAud);
  }
  if (stay.priceExclTax !== undefined) {
    return `${formatAmountWithAud(stay.priceExclTax, stay.currency, stay.priceExclTaxAud)} + tax`;
  }
  return 'No collected price';
}

export function lowestStayOf(property: CompetitorProperty): StayPrice {
  return {
    priceExclTax: property.lowestStayPriceExclTax,
    tax: property.lowestStayTax,
    priceInclTax: property.lowestStayPriceInclTax,
    currency: property.lowestStayCurrency,
    priceExclTaxAud: property.lowestStayPriceExclTaxAud,
    priceInclTaxAud: property.lowestStayPriceInclTaxAud,
  };
}

export function stayOf(option: CompetitorPropertyOption): StayPrice {
  return {
    priceExclTax: option.stayPriceExclTax,
    tax: option.stayTax,
    priceInclTax: option.stayPriceInclTax,
    currency: option.stayCurrency,
    priceExclTaxAud: option.stayPriceExclTaxAud,
    priceInclTaxAud: option.stayPriceInclTaxAud,
  };
}

export function stayBasis(nights: number | undefined, adults: number | undefined, checkIn: string | undefined): string {
  const parts = ['per room'];
  if (adults) {
    parts.push(plural(adults, 'adult'));
  }
  if (nights) {
    parts.push(plural(nights, 'night'));
  }
  if (checkIn) {
    parts.push(`from ${formatDate(checkIn)}`);
  }
  return parts.join(' · ');
}

export function cancellationText(option: CompetitorPropertyOption): string {
  if (option.cancellationText) {
    return option.cancellationText;
  }
  if (option.freeCancellationHours !== undefined) {
    return `Free cancellation until ${option.freeCancellationHours}h before check-in`;
  }
  if (option.freeChangeDays !== undefined) {
    return `Free date change until ${plural(option.freeChangeDays, 'day')} before check-in`;
  }
  if (option.refundable !== undefined) {
    return option.refundable ? 'Refundable' : 'Non-refundable';
  }
  return '—';
}

// A flash (svc-reservation) property's admin page; /accommodation/properties takes svc-accommodation ids.
export function flashPropertyPath(vendorId: string, lePropertyId: string) {
  return `/vendors/${encodeURIComponent(vendorId)}/properties/${encodeURIComponent(lePropertyId)}`;
}

export function competitorFlashStatusOf(property: CompetitorProperty): CompetitorFlashStatus {
  return property.inLatestCollection
    ? CompetitorFlashStatus.IN_LAST_COLLECTION
    : CompetitorFlashStatus.NOT_IN_LAST_COLLECTION;
}

export function sortByRecentlyCollected(properties: Array<CompetitorProperty>): Array<CompetitorProperty> {
  return [...properties].sort((a, b) => (b.lastCollectedAt ?? '').localeCompare(a.lastCollectedAt ?? ''));
}

export enum FilterField {
  HOTEL_NAME = 'hotelName',
  PROPERTY_ID = 'lePropertyId',
  FLASH_ID = 'leOfferIds',
  COMPETITOR = 'sourceName',
  ON_COMPETITOR = 'onCompetitor',
  ON_LE = 'onLe',
  MATCHED_TO_FLASH = 'matchedToFlash',
}

export const FILTER_FIELDS = Object.values(FilterField);

const FILTER_OPERATORS: Record<FilterField, string> = {
  [FilterField.HOTEL_NAME]: 'contains',
  [FilterField.PROPERTY_ID]: 'isAnyOf',
  [FilterField.FLASH_ID]: 'isAnyOf',
  [FilterField.COMPETITOR]: 'isAnyOf',
  [FilterField.ON_COMPETITOR]: 'is',
  [FilterField.ON_LE]: 'is',
  [FilterField.MATCHED_TO_FLASH]: 'is',
};

const FILTER_PARAMS: Record<FilterField, string> = {
  [FilterField.HOTEL_NAME]: 'hotelName',
  [FilterField.PROPERTY_ID]: 'propertyIds',
  [FilterField.FLASH_ID]: 'flashIds',
  [FilterField.COMPETITOR]: 'competitors',
  [FilterField.ON_COMPETITOR]: 'onCompetitor',
  [FilterField.ON_LE]: 'onLe',
  [FilterField.MATCHED_TO_FLASH]: 'matchedToFlash',
};

// Marks a URL whose On competitor filter was removed, so it doesn't fall back to the default.
const ANY_COMPETITOR_STATUS = 'all';

function filterItem(field: FilterField, value: unknown): GridFilterItem {
  return { id: field, field, operator: FILTER_OPERATORS[field], value };
}

export const DEFAULT_FILTER_MODEL: GridFilterModel = {
  items: [filterItem(FilterField.ON_COMPETITOR, CompetitorFlashStatus.IN_LAST_COLLECTION)],
};

// Splits pasted lists, so "a, b" is two ids; ids match whole and ignore case.
export function normaliseIds(values: unknown): Array<string> {
  const list = Array.isArray(values) ? values : [];
  const ids = list.flatMap((value) => String(value).split(/[\s,]+/)).map((id) => id.trim().toLowerCase());
  return [...new Set(ids.filter(Boolean))];
}

export function matchesHotelName(property: CompetitorProperty, search: string): boolean {
  return !!property.hotelName?.toLowerCase().includes(search.trim().toLowerCase());
}

export function matchesPropertyIds(property: CompetitorProperty, propertyIds: Array<string>): boolean {
  return property.lePropertyIds.some((id) => propertyIds.includes(id.toLowerCase()));
}

export function matchesFlashIds(property: CompetitorProperty, flashIds: Array<string>): boolean {
  return property.leOffers.some((offer) => flashIds.includes(offer.offerId.toLowerCase()));
}

// The item's value as URL text; undefined while the filter is still waiting for one.
function filterParam(item: GridFilterItem): string | undefined {
  switch (item.field) {
    case FilterField.HOTEL_NAME:
      return typeof item.value === 'string' && item.value.trim() ? item.value.trim() : undefined;
    case FilterField.PROPERTY_ID:
    case FilterField.FLASH_ID: {
      const ids = normaliseIds(item.value);
      return ids.length ? ids.join(',') : undefined;
    }
    case FilterField.COMPETITOR: {
      const sources = Array.isArray(item.value) ? item.value : [];
      return sources.length ? sources.join(',') : undefined;
    }
    case FilterField.ON_COMPETITOR:
    case FilterField.ON_LE:
    case FilterField.MATCHED_TO_FLASH:
      return item.value ? String(item.value) : undefined;
    default:
      return undefined;
  }
}

function paramValue(field: FilterField, param: string): unknown {
  switch (field) {
    case FilterField.HOTEL_NAME:
      return param.trim() || undefined;
    case FilterField.PROPERTY_ID:
    case FilterField.FLASH_ID: {
      const ids = normaliseIds([param]);
      return ids.length ? ids : undefined;
    }
    case FilterField.COMPETITOR: {
      const sources = param.split(',').filter((source) => (COMPETITOR_SOURCE_NAMES as Array<string>).includes(source));
      return sources.length ? sources : undefined;
    }
    case FilterField.ON_COMPETITOR:
      return Object.values(CompetitorFlashStatus).includes(param as CompetitorFlashStatus) ? param : undefined;
    case FilterField.ON_LE:
      return Object.keys(LE_FLASH_STATUS_LABELS).includes(param) ? param : undefined;
    case FilterField.MATCHED_TO_FLASH:
      return Object.values(FlashMatch).includes(param as FlashMatch) ? param : undefined;
    default:
      return undefined;
  }
}

export function activeFilters(model: GridFilterModel): Array<GridFilterItem> {
  return model.items.filter((item) => filterParam(item) !== undefined);
}

// One readable param per filter; the default On competitor is left out so a plain link shows the last collection.
export function filterModelToSearchParams(model: GridFilterModel): URLSearchParams {
  const params = new URLSearchParams();
  const items = activeFilters(model);
  FILTER_FIELDS.forEach((field) => {
    const param = filterParam(items.find((item) => item.field === field) ?? { field, operator: '' });
    if (field === FilterField.ON_COMPETITOR) {
      if (param !== CompetitorFlashStatus.IN_LAST_COLLECTION) {
        params.set(FILTER_PARAMS[field], param ?? ANY_COMPETITOR_STATUS);
      }
    } else if (param !== undefined) {
      params.set(FILTER_PARAMS[field], param);
    }
  });
  return params;
}

// Shared links can be stale or hand-edited, so values the page doesn't know are dropped.
export function searchParamsToFilterModel(params: URLSearchParams): GridFilterModel {
  const items = FILTER_FIELDS.flatMap((field) => {
    const param = params.get(FILTER_PARAMS[field]);
    if (field === FilterField.ON_COMPETITOR && param === null) {
      return DEFAULT_FILTER_MODEL.items;
    }
    const value = param === null ? undefined : paramValue(field, param);
    return value === undefined ? [] : [filterItem(field, value)];
  });
  return { items };
}

const MAX_CHIP_IDS = 2;

function describeIds(label: string, values: unknown): string {
  const ids = normaliseIds(values);
  const hidden = ids.length - MAX_CHIP_IDS;
  return `${label} is any of ${ids.slice(0, MAX_CHIP_IDS).join(', ')}${hidden > 0 ? ` +${hidden} more` : ''}`;
}

export function describeFilter(item: GridFilterItem): string {
  switch (item.field) {
    case FilterField.HOTEL_NAME:
      return `Hotel name contains “${String(item.value).trim()}”`;
    case FilterField.PROPERTY_ID:
      return describeIds('Property ID', item.value);
    case FilterField.FLASH_ID:
      return describeIds('Flash ID', item.value);
    case FilterField.COMPETITOR:
      return `Competitor is any of ${(item.value as Array<CompetitorSourceName>)
        .map((source) => COMPETITOR_SOURCE_LABELS[source])
        .join(', ')}`;
    case FilterField.ON_COMPETITOR:
      return `On competitor (Flash) is ${COMPETITOR_FLASH_STATUS_LABELS[item.value as CompetitorFlashStatus]}`;
    case FilterField.ON_LE:
      return `On LE (Flash) is ${LE_FLASH_STATUS_LABELS[item.value as LeFlashStatus]}`;
    case FilterField.MATCHED_TO_FLASH:
      return `Matched to Flash is ${FLASH_MATCH_LABELS[item.value as FlashMatch]}`;
    default:
      return item.field;
  }
}

// Filterable columns that don't have a filter yet; each column takes one.
export function unfilteredFields(items: Array<GridFilterItem>): Array<FilterField> {
  return FILTER_FIELDS.filter((field) => !items.some((item) => item.field === field));
}

function filterValue(model: GridFilterModel, field: FilterField): unknown {
  return activeFilters(model).find((item) => item.field === field)?.value;
}

export function flashMatchOf(property: CompetitorProperty): FlashMatch {
  return property.lePropertyIds.length ? FlashMatch.YES : FlashMatch.NO;
}

// The preset whose two filters the model still uses; other filters can change around it.
export function presetOf(model: GridFilterModel): CompetitorOffersPreset | undefined {
  const onCompetitor = filterValue(model, FilterField.ON_COMPETITOR);
  const onLe = filterValue(model, FilterField.ON_LE);
  return COMPETITOR_OFFERS_PRESETS.find((preset) => preset.onCompetitor === onCompetitor && preset.onLe === onLe)
    ?.value;
}

// Sets both filters to the preset's; with no preset (Custom) only the On LE filter is dropped.
export function withPreset(model: GridFilterModel, preset: CompetitorOffersPreset | undefined): GridFilterModel {
  const option = COMPETITOR_OFFERS_PRESETS.find((candidate) => candidate.value === preset);
  if (!option) {
    return { ...model, items: model.items.filter((item) => item.field !== FilterField.ON_LE) };
  }
  const items = model.items.filter(
    (item) => item.field !== FilterField.ON_COMPETITOR && item.field !== FilterField.ON_LE,
  );
  return {
    ...model,
    items: [
      ...items,
      filterItem(FilterField.ON_COMPETITOR, option.onCompetitor),
      filterItem(FilterField.ON_LE, option.onLe),
    ],
  };
}

// The hotel as one past collection saw it: that collection's deal, prices and room options.
export function withSnapshot(property: CompetitorProperty, snapshot: CompetitorPropertySnapshot): CompetitorProperty {
  const [lowest] = snapshot.options
    .filter((option) => option.stayPriceInclTaxAud !== undefined || option.stayPriceExclTaxAud !== undefined)
    .sort(
      (a, b) =>
        (a.stayPriceInclTaxAud ?? Infinity) - (b.stayPriceInclTaxAud ?? Infinity) ||
        (a.stayPriceExclTaxAud ?? Infinity) - (b.stayPriceExclTaxAud ?? Infinity),
    );
  return {
    ...property,
    externalDealId: snapshot.externalDealId,
    dealUrl: snapshot.dealUrl,
    saleStart: snapshot.saleStart,
    saleEnd: snapshot.saleEnd,
    leadPrice: snapshot.leadPrice,
    wasPrice: snapshot.wasPrice,
    leadCurrency: snapshot.leadCurrency,
    leadPriceIncludesTax: snapshot.leadPriceIncludesTax,
    leadPriceAud: snapshot.leadPriceAud,
    wasPriceAud: snapshot.wasPriceAud,
    discountPct: snapshot.discountPct,
    leadPriceBasis: snapshot.leadPriceBasis,
    optionCount: snapshot.options.length,
    lowestStayPriceExclTax: lowest?.stayPriceExclTax,
    lowestStayTax: lowest?.stayTax,
    lowestStayPriceInclTax: lowest?.stayPriceInclTax,
    lowestStayCurrency: lowest?.stayCurrency,
    lowestStayPriceExclTaxAud: lowest?.stayPriceExclTaxAud,
    lowestStayTaxAud: lowest?.stayTaxAud,
    lowestStayPriceInclTaxAud: lowest?.stayPriceInclTaxAud,
    lowestStayNights: lowest?.stayNights,
    lowestStayAdults: lowest?.adults,
    lowestStayCheckIn: lowest?.stayCheckIn,
    lowestStayQuoted: lowest?.stayPriceQuoted,
  };
}

export function offerPath(offerId: string) {
  return `/offers/${encodeURIComponent(offerId)}`;
}

const DATE_PARAM_FORMAT = 'YYYY-MM-DD';

// The opportunities range is left out while it's the default, so a plain link follows the latest two weeks.
export function pageSearchParams(model: GridFilterModel, [from, to]: [Dayjs, Dayjs]): URLSearchParams {
  const params = filterModelToSearchParams(model);
  const [defaultFrom, defaultTo] = getDefaultDateRange();
  if (!from.isSame(defaultFrom, 'day') || !to.isSame(defaultTo, 'day')) {
    params.set('from', from.format(DATE_PARAM_FORMAT));
    params.set('to', to.format(DATE_PARAM_FORMAT));
  }
  return params;
}

export function toDateParam(date: Dayjs): string {
  return date.format(DATE_PARAM_FORMAT);
}

// 15–28 Oct 2026, 28 Sep – 12 Oct 2026, or 28 Dec 2025 – 3 Jan 2026.
export function formatDateRange(first: string, last: string): string {
  const start = dayjs(first);
  const end = dayjs(last);
  if (start.isSame(end, 'day')) {
    return end.format(DATE_FORMAT);
  }
  if (start.isSame(end, 'month')) {
    return `${start.format('D')}–${end.format(DATE_FORMAT)}`;
  }
  return `${start.format(start.isSame(end, 'year') ? 'D MMM' : DATE_FORMAT)} – ${end.format(DATE_FORMAT)}`;
}

export function formatShare(part: number, total: number): string {
  const share = total ? (part / total) * 100 : 0;
  return share > 0 && share < 1 ? '<1%' : `${Math.round(share)}%`;
}

export type HotelOpportunitiesState =
  | { status: 'loading' | 'error' }
  | { status: 'loaded'; opportunities?: CompetitorHotelOpportunities };

export type CompetitorOfferRow = CompetitorProperty & { gha: HotelOpportunitiesState };

// Missing opportunities sort last in both directions.
export function compareOpportunities(a: number | undefined, b: number | undefined, direction: 'asc' | 'desc') {
  if (a === undefined || b === undefined) {
    return (a === undefined ? 1 : 0) - (b === undefined ? 1 : 0);
  }
  return direction === 'asc' ? a - b : b - a;
}

// Each Flash property's vendor, from the primary property and the offers; the property page needs it.
export function flashPropertyVendors(property: CompetitorProperty): Map<string, string | undefined> {
  const vendors = new Map<string, string | undefined>(property.lePropertyIds.map((id) => [id, undefined]));
  property.leOffers.forEach((offer) => offer.leVendorId && vendors.set(offer.lePropertyId, offer.leVendorId));
  if (property.lePropertyId && property.leVendorId) {
    vendors.set(property.lePropertyId, property.leVendorId);
  }
  return vendors;
}

// With no past collection chosen, the page shows the latest one that found the hotel (history is newest first).
export function shownCollectionId(
  history: Array<CompetitorPropertyHistoryEntry>,
  selectedCollectionId: string | undefined,
): string | undefined {
  return selectedCollectionId ?? history.find((entry) => entry.seen)?.collectionId;
}

// Picking the collection on show, or the latest one, goes back to the latest.
export function collectionToSelect(
  collectionId: string,
  history: Array<CompetitorPropertyHistoryEntry>,
  selectedCollectionId: string | undefined,
): string | undefined {
  return collectionId === shownCollectionId(history, selectedCollectionId) ||
    collectionId === shownCollectionId(history, undefined)
    ? undefined
    : collectionId;
}
